import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { PlusCircle, Wallet } from "lucide-react";
import { getPortfolio, tokenLogoUrl } from "@/lib/logos";
import { useWallet } from "@/contexts/WalletContext";
import { TokenLogo } from "@/components/common/TokenLogo";
import { TestTokenNote, TEST_TOKEN_NOTE } from "@/components/common/TestTokenNote";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const usd = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const amount = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 6 });
const idOf = (a) => a.symbol.toLowerCase();

const SUMMARY_TEST_IDS = { FMI: "nav-wallet-balance", USDT: "nav-wallet-usdt-balance" };

export const usePortfolio = (address) =>
  useQuery({ queryKey: ["portfolio", address?.toLowerCase()], queryFn: () => getPortfolio(address), enabled: Boolean(address), refetchInterval: 6000,
    placeholderData: keepPreviousData, retry: 3, retryDelay: 1000 });

const AddToWallet = ({ asset }) => {
  const { addUSDTToken } = useWallet();
  if (asset.type !== "erc20") return null;
  const add = async () => {
    const ok = await addUSDTToken(asset.address, asset.decimals, asset.symbol, tokenLogoUrl(asset.address));
    toast[ok ? "success" : "error"](ok ? `${asset.symbol} added to your wallet` : "Could not add the token to your wallet");
  };
  return (
    <button data-testid={`portfolio-add-${idOf(asset)}`} onClick={add} title={`Add ${asset.symbol} to MetaMask`}
      className="text-zinc-500 hover:text-[#D4AF37] transition-colors"><PlusCircle size={15} /></button>
  );
};

const AssetRow = ({ asset, compact, balanceTestId }) => (
  <div data-testid={`portfolio-asset-${idOf(asset)}`} className="flex items-center gap-3 py-2">
    <TokenLogo address={asset.address} symbol={asset.symbol} size={compact ? "sm" : "md"} version={asset.logoVersion} />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-white truncate">{asset.symbol}</p>
      {!compact && <p className="text-xs text-zinc-500 truncate">{asset.name}</p>}
    </div>
    <div className="text-right">
      <p data-testid={balanceTestId || `portfolio-balance-${idOf(asset)}`} className="text-sm font-mono text-white">{amount(asset.balance)}</p>
      <p data-testid={`portfolio-usd-${idOf(asset)}`} className="text-xs font-mono text-zinc-500">{asset.valueUsd == null ? "no price" : usd(asset.valueUsd)}</p>
    </div>
    {!compact && <AddToWallet asset={asset} />}
  </div>
);

const Total = ({ data, compact }) => (
  <div>
    <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Total balance</p>
    <p data-testid="portfolio-total-usd" className={`font-display font-bold text-white ${compact ? "text-lg" : "text-3xl"}`}>{usd(data.totalUsd)}</p>
    {data.fmiPriceUsd != null && <p className="text-[11px] text-zinc-500 font-mono">1 FMI = ${Number(data.fmiPriceUsd).toFixed(4)} · live pool price</p>}
  </div>
);

/** Compact list for the wallet dropdown. */
export const PortfolioSummary = () => {
  const { address } = useWallet();
  const { data, isError } = usePortfolio(address);
  return (
    <div data-testid="portfolio-summary" className="px-3 py-2 border-b border-[#2A2411]">
      {isError && !data && <p className="text-xs text-zinc-500">Balances unavailable</p>}
      {!data && !isError && <div className="h-12 rounded bg-[#050505] animate-pulse" />}
      {data && (
        <>
          <Total data={data} compact />
          <div className="divide-y divide-[#2A2411]/60 mt-1">{data.assets.slice(0, 4).map((a) => <AssetRow key={a.address || "fmi"} asset={a} compact balanceTestId={SUMMARY_TEST_IDS[a.symbol]} />)}</div>
          {data.assets.length > 4 && <p className="text-[11px] text-zinc-500 pt-1">+{data.assets.length - 4} more in My Tokens</p>}
          <p data-testid="portfolio-summary-test-token-note" className="text-[11px] text-amber-300/80 pt-1">{TEST_TOKEN_NOTE}</p>
        </>
      )}
    </div>
  );
};

/** Full portfolio card (My Tokens page). */
export const PortfolioCard = ({ address }) => {
  const { data, isError } = usePortfolio(address);
  return (
    <Card data-testid="portfolio-card" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-display flex items-center gap-2"><Wallet size={16} className="text-[#D4AF37]" /> Portfolio</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isError && !data && <p data-testid="portfolio-error" className="text-sm text-zinc-500">Portfolio unavailable</p>}
        {!data && !isError && <div className="h-28 rounded bg-[#050505] animate-pulse" />}
        {data && (
          <>
            <Total data={data} />
            <div className="divide-y divide-[#2A2411]">{data.assets.map((a) => <AssetRow key={a.address || "fmi"} asset={a} />)}</div>
            {data.unpricedCount > 0 && <p className="text-xs text-zinc-600">Tokens without a market price are not included in the total.</p>}
            <TestTokenNote testId="portfolio-test-token-note" />
          </>
        )}
      </CardContent>
    </Card>
  );
};
